import { useState, useEffect, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  BarChart3, Download, Calendar, TrendingUp, TrendingDown,
  DollarSign, ShoppingBag, Users, Clock, RefreshCw, Filter,
  CreditCard, Trash2, Tag, FileText, ChevronLeft, ChevronRight,
  ArrowUpRight, ArrowDownRight, Receipt, Percent, Package,
  AlertTriangle, Printer, Eye, ShieldAlert, Send, MessageSquare,
  Bell, Phone, Smartphone, CheckCircle2, XCircle, Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDateTime, formatDay } from '@/lib/restaurantTime';

import EndOfDayReport from './EndOfDayReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───── helpers ───── */
const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;

/* ───── Period Select ───── */
function PeriodSelect({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-36" data-testid="period-select">
        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="today">Today</SelectItem>
        <SelectItem value="yesterday">Yesterday</SelectItem>
        <SelectItem value="week">Last 7 Days</SelectItem>
        <SelectItem value="month">Last 30 Days</SelectItem>
        <SelectItem value="quarter">Last 90 Days</SelectItem>
      </SelectContent>
    </Select>
  );
}

/* ───── Stat Card ───── */
function StatCard({ label, value, sub, icon: Icon, trend }) {
  return (
    <Card className="bg-white border border-gray-100 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {fmtPct(Math.abs(trend))} vs prev period
              </div>
            )}
          </div>
          {Icon && (
            <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 ml-3">
              <Icon className="h-4 w-4 text-blue-600" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════ Sales Overview Tab ═══════════════════ */
function SalesOverviewTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/sales-overview?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No data available</div>;

  return (
    <div className="space-y-4" data-testid="sales-overview-tab">
      {data.period?.first_day && (
        <p className="text-xs text-gray-500" data-testid="sales-overview-period">
          {data.period.first_day === data.period.last_day ? formatDay(data.period.first_day)
            : `${formatDay(data.period.first_day)} - ${formatDay(data.period.last_day)}`} · {data.timezone}
        </p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Revenue" value={fmt(data.revenue)} icon={DollarSign} trend={data.change_pct}
          sub={`${fmt(data.money_taken)} taken, less refunds & voids`} />
        <StatCard label="Gross Sales" value={fmt(data.gross_sales)} icon={TrendingUp} />
        <StatCard label="Net Sales" value={fmt(data.net_sales)} icon={TrendingUp} />
        <StatCard label="Total Orders" value={data.total_orders} icon={ShoppingBag} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Avg Order Value" value={fmt(data.avg_order_value)} icon={Receipt} />
        <StatCard label="Tax Collected" value={fmt(data.total_tax)} icon={FileText} />
        <StatCard label="Tips" value={fmt(data.total_tips)} icon={DollarSign} />
        <StatCard label="Discounts Given" value={fmt(data.total_discounts)} icon={Tag} />
        <StatCard label="Refunds & Voids" value={fmt((data.total_refunds || 0) + (data.void_amount || 0))} icon={Trash2} sub={`${data.refunded_orders} refunded, ${data.voided_orders} voided`} />
      </div>
    </div>
  );
}

/* ═══════════════════ Sales Report Tab ═══════════════════ */
function SalesReportTab({ token, period }) {
  const [data, setData] = useState({ orders: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/sales-report?period=${period}&limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData({ orders: [], total: 0 }); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="sales-report-tab">
      <div className="text-sm text-gray-500">{data.total} orders found</div>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">Order #</th>
                <th className="text-left p-3 font-medium text-gray-600">Type</th>
                <th className="text-left p-3 font-medium text-gray-600">Status</th>
                <th className="text-right p-3 font-medium text-gray-600">Subtotal</th>
                <th className="text-right p-3 font-medium text-gray-600">Tax</th>
                <th className="text-right p-3 font-medium text-gray-600">Tip</th>
                <th className="text-right p-3 font-medium text-gray-600">Discount</th>
                <th className="text-right p-3 font-medium text-gray-600">Total</th>
                <th className="text-left p-3 font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-mono">#{o.id}</td>
                  <td className="p-3"><Badge variant="outline" className="capitalize text-xs">{o.order_type}</Badge></td>
                  <td className="p-3">
                    <Badge className={`text-xs ${o.is_voided ? 'bg-red-100 text-red-700' : o.is_refunded ? 'bg-orange-100 text-orange-700' : o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {o.is_voided ? 'Voided' : o.is_refunded ? 'Refunded' : o.payment_status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">{fmt(o.subtotal)}</td>
                  <td className="p-3 text-right">{fmt(o.tax)}</td>
                  <td className="p-3 text-right">{fmt(o.tip)}</td>
                  <td className="p-3 text-right">{o.discount > 0 ? `-${fmt(o.discount)}` : '-'}</td>
                  <td className="p-3 text-right font-semibold">{fmt(o.total)}</td>
                  <td className="p-3 text-xs text-gray-500">{formatDateTime(o.created_at, data.timezone)}</td>
                </tr>
              ))}
              {data.orders.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-400">No orders found for this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════ Tender & Card Types Tab ═══════════════════ */
function TenderCardTypesTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/tender-card-types?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No transaction data</div>;

  const methodLabels = { card: 'Card', cash: 'Cash', gift_card: 'Gift Card', zelle: 'Zelle', venmo: 'Venmo', crypto: 'Crypto', loyalty: 'Loyalty', other: 'Other' };
  const cardColors = { visa: 'bg-blue-100 text-blue-800', mastercard: 'bg-orange-100 text-orange-800', amex: 'bg-indigo-100 text-indigo-800', discover: 'bg-amber-100 text-amber-800' };

  return (
    <div className="space-y-6" data-testid="tender-card-types-tab">
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Total Transactions" value={data.total_transactions} icon={CreditCard} />
        <StatCard label="Total Amount" value={fmt(data.total_amount)} icon={DollarSign} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">By Payment Method</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.by_payment_method.map((m, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <CreditCard className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="text-sm font-medium capitalize">{methodLabels[m.method] || m.method}</span>
                <Badge variant="outline" className="text-xs">{m.count} txns</Badge>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold">{fmt(m.amount)}</span>
                {m.tip > 0 && <span className="text-xs text-gray-400 ml-2">(+{fmt(m.tip)} tips)</span>}
              </div>
            </div>
          ))}
          {data.by_payment_method.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No transactions</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">By Card Type</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.by_card_type.map((c, i) => (
            <div key={i} className="flex items-center justify-between">
              <Badge className={`capitalize ${cardColors[c.card_type] || 'bg-gray-100 text-gray-800'}`}>{c.card_type}</Badge>
              <div className="text-right">
                <span className="text-sm font-semibold">{fmt(c.amount)}</span>
                <span className="text-xs text-gray-400 ml-2">({c.count})</span>
              </div>
            </div>
          ))}
          {data.by_card_type.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No card transactions</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">By Entry Mode</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.by_entry_mode.map((e, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-sm capitalize">{e.entry_mode}</span>
              <div className="text-right">
                <span className="text-sm font-semibold">{fmt(e.amount)}</span>
                <span className="text-xs text-gray-400 ml-2">({e.count})</span>
              </div>
            </div>
          ))}
          {data.by_entry_mode.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No data</p>}
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════ Item Sales Tab ═══════════════════ */
function ItemSalesTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('quantity');

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/item-sales?period=${period}&sort_by=${sortBy}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, sortBy, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No item sales data</div>;

  return (
    <div className="space-y-4" data-testid="item-sales-tab">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 gap-3 flex-1 mr-4">
          <StatCard label="Items Sold" value={data.total_items_sold} icon={Package} />
          <StatCard label="Total Revenue" value={fmt(data.total_revenue)} icon={DollarSign} />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="quantity">By Quantity</SelectItem>
            <SelectItem value="revenue">By Revenue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">#</th>
                <th className="text-left p-3 font-medium text-gray-600">Item Name</th>
                <th className="text-right p-3 font-medium text-gray-600">Qty Sold</th>
                <th className="text-right p-3 font-medium text-gray-600">Revenue</th>
                <th className="text-right p-3 font-medium text-gray-600">Avg Price</th>
                <th className="text-right p-3 font-medium text-gray-600">% of Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3 text-gray-400">{i + 1}</td>
                  <td className="p-3 font-medium">{item.name}</td>
                  <td className="p-3 text-right">{item.quantity}</td>
                  <td className="p-3 text-right font-semibold">{fmt(item.revenue)}</td>
                  <td className="p-3 text-right">{fmt(item.avg_price)}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Progress value={item.pct_of_revenue} className="w-16 h-1.5" />
                      <span className="text-xs text-gray-500 w-10 text-right">{fmtPct(item.pct_of_revenue)}</span>
                    </div>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-gray-400">No items sold in this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════ Removed Items Tab (Fraud Detection — Clover-style) ═══════════════════ */
function RemovedItemsTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('summary'); // summary, by_employee, by_reason, all_items

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const params = `period=${period}${filterType !== 'all' ? `&filter_type=${filterType}` : ''}`;
      const r = await axios.get(`${API}/reports/removed-items?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, filterType, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No data available</div>;

  const s = data.summary;
  const actionColors = { item_removed: 'bg-red-100 text-red-700', order_voided: 'bg-orange-100 text-orange-700', discount_applied: 'bg-yellow-100 text-yellow-700' };
  const actionLabels = { item_removed: 'Removed', order_voided: 'Voided', discount_applied: 'Discount' };

  return (
    <div className="space-y-4" data-testid="removed-items-tab">
      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44" data-testid="removed-items-filter">
            <Filter className="h-4 w-4 mr-2 text-gray-400" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="printed">Printed (After Kitchen)</SelectItem>
            <SelectItem value="unprinted">Unprinted (Before Kitchen)</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-1 bg-gray-100 p-0.5 rounded-md">
          {[
            { id: 'summary', label: 'Summary' },
            { id: 'by_employee', label: 'By Employee' },
            { id: 'by_reason', label: 'By Reason' },
            { id: 'all_items', label: 'All Events' },
          ].map(m => (
            <button key={m.id} onClick={() => setViewMode(m.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors
                ${viewMode === m.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`removed-view-${m.id}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Clover-style summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-red-50 border-red-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Printer className="h-4 w-4 text-red-500" /><span className="text-xs text-red-600 font-medium">PRINTED (After Kitchen)</span></div>
            <p className="text-2xl font-bold text-red-700">{s.printed_items}</p>
            <p className="text-xs text-red-500">{fmt(s.printed_value)} value</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><FileText className="h-4 w-4 text-amber-500" /><span className="text-xs text-amber-600 font-medium">UNPRINTED (Before Kitchen)</span></div>
            <p className="text-2xl font-bold text-amber-700">{s.unprinted_items}</p>
            <p className="text-xs text-amber-500">{fmt(s.unprinted_value)} value</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Trash2 className="h-4 w-4 text-gray-500" /><span className="text-xs text-gray-600 font-medium">DELETED ORDERS</span></div>
            <p className="text-2xl font-bold text-gray-700">{s.deleted_orders}</p>
            <p className="text-xs text-gray-500">{fmt(s.total_voided_value)} value</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><ShieldAlert className="h-4 w-4 text-green-600" /><span className="text-xs text-green-700 font-medium">TOTAL VALUE REMOVED</span></div>
            <p className="text-2xl font-bold text-green-800">{fmt(s.total_value_removed)}</p>
            <p className="text-xs text-green-600">{s.total_events} events total</p>
          </CardContent>
        </Card>
      </div>

      {/* Action type mini-stats */}
      <div className="flex gap-3">
        <Badge variant="outline" className="text-xs"><Trash2 className="h-3 w-3 mr-1" /> {s.item_removals} items removed</Badge>
        <Badge variant="outline" className="text-xs"><AlertTriangle className="h-3 w-3 mr-1" /> {s.void_events} voids</Badge>
        <Badge variant="outline" className="text-xs"><Tag className="h-3 w-3 mr-1" /> {s.discount_events} discounts</Badge>
        <Badge variant="outline" className="text-xs"><Receipt className="h-3 w-3 mr-1" /> {s.refunded_orders} refunds</Badge>
      </div>

      {/* VIEW: Summary (default) */}
      {viewMode === 'summary' && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Top Employees by Events</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.by_employee.slice(0, 5).map((emp, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <div>
                    <span className="text-sm font-medium">{emp.name}</span>
                    <div className="flex gap-1 mt-0.5">
                      {emp.printed_events > 0 && <Badge className="text-[10px] bg-red-100 text-red-700">{emp.printed_events} printed</Badge>}
                      {emp.unprinted_events > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-700">{emp.unprinted_events} unprinted</Badge>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold">{fmt(emp.total_value)}</span>
                    <span className="text-xs text-gray-400 block">{emp.total_events} events</span>
                  </div>
                </div>
              ))}
              {data.by_employee.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No events</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Top Reasons</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {data.by_reason.slice(0, 8).map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <span className="text-sm">{r.reason}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{r.count}x</Badge>
                    <span className="text-sm font-semibold">{fmt(r.value)}</span>
                  </div>
                </div>
              ))}
              {data.by_reason.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No events</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* VIEW: By Employee */}
      {viewMode === 'by_employee' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Employee</th>
                  <th className="text-center p-3 font-medium text-gray-600">Total Events</th>
                  <th className="text-center p-3 font-medium text-red-600">Printed</th>
                  <th className="text-center p-3 font-medium text-amber-600">Unprinted</th>
                  <th className="text-center p-3 font-medium text-gray-600">Removals</th>
                  <th className="text-center p-3 font-medium text-gray-600">Discounts</th>
                  <th className="text-center p-3 font-medium text-gray-600">Voids</th>
                  <th className="text-right p-3 font-medium text-gray-600">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {data.by_employee.map((emp, i) => (
                  <tr key={i} className={`border-b hover:bg-gray-50 ${emp.printed_events > 3 ? 'bg-red-50/50' : ''}`}>
                    <td className="p-3 font-medium">{emp.name}</td>
                    <td className="p-3 text-center">{emp.total_events}</td>
                    <td className="p-3 text-center"><Badge className={`text-xs ${emp.printed_events > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>{emp.printed_events}</Badge></td>
                    <td className="p-3 text-center"><Badge className="text-xs bg-amber-100 text-amber-700">{emp.unprinted_events}</Badge></td>
                    <td className="p-3 text-center">{emp.item_removals}</td>
                    <td className="p-3 text-center">{emp.discounts}</td>
                    <td className="p-3 text-center">{emp.voids}</td>
                    <td className="p-3 text-right font-semibold">{fmt(emp.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* VIEW: By Reason */}
      {viewMode === 'by_reason' && (
        <Card>
          <CardContent className="p-4 space-y-3">
            {data.by_reason.map((r, i) => {
              const maxCount = Math.max(...data.by_reason.map(x => x.count), 1);
              return (
                <div key={i}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">{r.reason}</span>
                    <div className="text-right text-sm">
                      <span className="font-semibold">{fmt(r.value)}</span>
                      <span className="text-gray-400 ml-2">({r.count}x)</span>
                    </div>
                  </div>
                  <Progress value={(r.count / maxCount) * 100} className="h-2" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* VIEW: All Events (individual items) */}
      {viewMode === 'all_items' && (
        <div className="space-y-2">
          {data.items.map((item, i) => (
            <Card key={i} className={`${item.is_printed ? 'border-l-4 border-l-red-400' : 'border-l-4 border-l-amber-300'}`}>
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{item.item_name}</span>
                      {item.quantity > 1 && <span className="text-xs text-gray-400">x{item.quantity}</span>}
                      <Badge className={`text-[10px] ${actionColors[item.action] || 'bg-gray-100 text-gray-700'}`}>
                        {actionLabels[item.action] || item.action}
                      </Badge>
                      {item.is_printed
                        ? <Badge className="text-[10px] bg-red-100 text-red-700"><Printer className="h-2.5 w-2.5 mr-0.5" />Printed</Badge>
                        : <Badge className="text-[10px] bg-amber-100 text-amber-700">Unprinted</Badge>
                      }
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-medium text-gray-700">{item.employee_name}</span>
                      {item.approved_by && <span className="ml-2 text-green-600">(Approved: {item.approved_by})</span>}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Reason: <span className="text-gray-600 italic">{item.reason}</span></p>
                    {item.order_id && <p className="text-xs text-gray-400">Order #{item.order_id}</p>}
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    <span className="text-base font-bold">{fmt(item.total_value)}</span>
                    <p className="text-xs text-gray-400">{item.created_at ? formatDateTime(item.created_at, data?.timezone) : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {data.items.length === 0 && <div className="py-12 text-center text-gray-400">No removed items in this period</div>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ Order Types Tab ═══════════════════ */
function OrderTypesTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/order-types-report?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No order type data</div>;

  const typeLabels = { dine_in: 'Dine-In', takeout: 'Takeout', pickup: 'Pickup', delivery: 'Delivery', online: 'Online' };
  const typeColors = { dine_in: 'bg-blue-500', takeout: 'bg-green-500', pickup: 'bg-amber-500', delivery: 'bg-purple-500', online: 'bg-pink-500' };

  return (
    <div className="space-y-4" data-testid="order-types-tab">
      <StatCard label="Total Orders" value={data.total_orders} icon={ShoppingBag} />

      <Card>
        <CardContent className="p-5 space-y-4">
          {data.order_types.map((ot, i) => (
            <div key={i}>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${typeColors[ot.type] || 'bg-gray-400'}`} />
                  <span className="text-sm font-medium capitalize">{typeLabels[ot.type] || ot.type}</span>
                </div>
                <div className="text-right text-sm">
                  <span className="font-semibold">{fmt(ot.total)}</span>
                  <span className="text-gray-400 ml-2">({ot.count} orders)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={ot.pct} className="flex-1 h-2" />
                <span className="text-xs text-gray-500 w-10 text-right">{fmtPct(ot.pct)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                <span>Avg: {fmt(ot.avg_order)}</span>
                <span>Tips: {fmt(ot.tips)}</span>
              </div>
            </div>
          ))}
          {data.order_types.length === 0 && <p className="text-center text-gray-400 py-4">No orders</p>}
        </CardContent>
      </Card>
    </div>
  );
}

/* ═══════════════════ Employees Tab ═══════════════════ */
function EmployeesTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/employees?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No employee data</div>;

  const maxSales = Math.max(...data.employees.map(e => e.sales_amount), 1);

  return (
    <div className="space-y-4" data-testid="employees-tab">
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Total Staff" value={data.total_staff} icon={Users} />
        <StatCard label="Active This Period" value={data.active_in_period} icon={Clock} />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">Employee</th>
                <th className="text-left p-3 font-medium text-gray-600">Role</th>
                <th className="text-right p-3 font-medium text-gray-600">Sales</th>
                <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                <th className="text-right p-3 font-medium text-gray-600">Tips</th>
                <th className="text-right p-3 font-medium text-gray-600">Avg Txn</th>
                <th className="p-3 font-medium text-gray-600">Performance</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{emp.name}</td>
                  <td className="p-3"><Badge variant="outline" className="text-xs capitalize">{emp.role}</Badge></td>
                  <td className="p-3 text-right">{emp.sales_count}</td>
                  <td className="p-3 text-right font-semibold">{fmt(emp.sales_amount)}</td>
                  <td className="p-3 text-right">{fmt(emp.tips)}</td>
                  <td className="p-3 text-right">{fmt(emp.avg_transaction)}</td>
                  <td className="p-3">
                    <Progress value={(emp.sales_amount / maxSales) * 100} className="h-2" />
                  </td>
                </tr>
              ))}
              {data.employees.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-gray-400">No employee activity</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════ Discounts Tab ═══════════════════ */
function DiscountsTab({ token, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/discounts?period=${period}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch { setData(null); }
    setLoading(false);
  });

  useEffect(() => { load(); }, [period, load]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No discount data</div>;

  return (
    <div className="space-y-4" data-testid="discounts-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Discounted Orders" value={data.discounted_orders} icon={Tag} sub={`of ${data.total_orders} total`} />
        <StatCard label="Discount Rate" value={fmtPct(data.discount_rate)} icon={Percent} />
        <StatCard label="Total Discounts" value={fmt(data.total_discount_value)} icon={DollarSign} />
        <StatCard label="Avg Discount" value={fmt(data.avg_discount_per_order)} icon={Receipt} />
      </div>

      {data.promo_codes.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Promo Codes Used</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {data.promo_codes.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-800 font-mono">{p.code}</Badge>
                  <span className="text-xs text-gray-400">{p.uses} uses</span>
                </div>
                <span className="font-semibold text-sm">{fmt(p.total_discount)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Recent Discounted Orders</CardTitle></CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-gray-600">Order #</th>
                <th className="text-right p-3 font-medium text-gray-600">Total</th>
                <th className="text-right p-3 font-medium text-gray-600">Discount</th>
                <th className="text-left p-3 font-medium text-gray-600">Promo</th>
                <th className="text-left p-3 font-medium text-gray-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.discounted_order_list.map((o, i) => (
                <tr key={i} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-mono">#{o.order_id}</td>
                  <td className="p-3 text-right">{fmt(o.total)}</td>
                  <td className="p-3 text-right text-red-600">-{fmt(o.discount)}</td>
                  <td className="p-3"><Badge variant="outline" className="text-xs">{o.promo_code}</Badge></td>
                  <td className="p-3 text-xs text-gray-500">{formatDateTime(o.created_at, data.timezone)}</td>
                </tr>
              ))}
              {data.discounted_order_list.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-gray-400">No discounted orders</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════ Notification Setup Tab ═══════════════════ */
function NotificationSetupTab({ token }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);

  const loadConfig = useLatestCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/fraud-alerts/config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(r.data);
    } catch { setConfig(null); }
    setLoading(false);
  });

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/reports/fraud-alerts/config`, config, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success('Notification settings saved');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const testTelegram = async () => {
    setTestingTelegram(true);
    try {
      await axios.post(`${API}/reports/fraud-alerts/telegram/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Test message sent to Telegram!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test message');
    }
    setTestingTelegram(false);
  };

  const testSms = async () => {
    setTestingSms(true);
    try {
      await axios.post(`${API}/reports/fraud-alerts/sms/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Test SMS sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test SMS');
    }
    setTestingSms(false);
  };

  const discoverChats = async () => {
    setDiscovering(true);
    try {
      const r = await axios.get(`${API}/reports/fraud-alerts/telegram/chats`, { headers: { Authorization: `Bearer ${token}` } });
      const chats = r.data.chats || [];
      if (chats.length === 0) {
        toast.info('No chats found. Send /start to @chain29OFAbot first, then try again.');
      } else {
        const first = chats[0];
        setConfig(prev => ({ ...prev, telegram_chat_id: first.chat_id }));
        toast.success(`Found chat: ${first.name} (${first.chat_id})`);
      }
    } catch { toast.error('Failed to discover chats'); }
    setDiscovering(false);
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await axios.post(`${API}/reports/fraud-alerts/digest/send-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const d = r.data.delivery;
      const parts = [];
      if (d.telegram === 'sent') parts.push('Telegram');
      if (d.sms === 'sent') parts.push('SMS');
      toast.success(`Digest sent via ${parts.join(' & ')}!`);
      setDigestPreview(r.data.digest);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    }
    setSendingDigest(false);
  };

  const loadDigestPreview = async () => {
    try {
      const r = await axios.get(`${API}/reports/fraud-alerts/digest/preview`, { headers: { Authorization: `Bearer ${token}` } });
      setDigestPreview(r.data);
    } catch { toast.error('Failed to load digest preview'); }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!config) return <div className="py-12 text-center text-gray-400">No configuration found</div>;

  return (
    <div className="space-y-5" data-testid="notification-setup-tab">
      <div className="mb-2">
        <h2 className="text-base font-semibold text-gray-900">Notification Channels</h2>
        <p className="text-xs text-gray-500">Configure how you receive fraud and loss prevention alerts</p>
      </div>

      {/* ──── Telegram Channel ──── */}
      <Card className={`border-2 transition-colors ${config.alert_via_telegram ? 'border-blue-300 bg-blue-50/20' : 'border-gray-200'}`} data-testid="telegram-channel-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.alert_via_telegram ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <MessageSquare className={`h-5 w-5 ${config.alert_via_telegram ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Telegram Bot</h3>
                <p className="text-xs text-gray-500">Instant alerts via @chain29OFAbot</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="telegram-toggle">
              <input type="checkbox" className="sr-only peer" checked={config.alert_via_telegram || false}
                onChange={e => setConfig({...config, alert_via_telegram: e.target.checked})} />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="space-y-3">
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">Quick Setup</p>
              <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside mb-3">
                <li>Open Telegram and search for <a href="https://t.me/chain29OFAbot" target="_blank" rel="noreferrer" className="text-blue-600 font-medium underline">@chain29OFAbot</a></li>
                <li>Send <code className="bg-gray-100 px-1 rounded text-[11px]">/start</code> to the bot</li>
                <li>Click <strong>Auto-detect</strong> below to link your chat</li>
              </ol>
              <div className="flex items-center gap-2">
                <input type="text" value={config.telegram_chat_id || ''} onChange={e => setConfig({...config, telegram_chat_id: e.target.value || null})}
                  placeholder="Chat ID (auto-filled)"
                  className="flex-1 border rounded-md px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-200 outline-none transition" data-testid="telegram-chat-id-input" />
                <Button variant="outline" size="sm" onClick={discoverChats} disabled={discovering} data-testid="discover-chats-btn" className="shrink-0">
                  {discovering ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : 'Auto-detect'}
                </Button>
              </div>
            </div>

            {config.telegram_chat_id && (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-xs text-green-700 font-medium">Connected (Chat: {config.telegram_chat_id})</span>
                </div>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={testTelegram} disabled={testingTelegram} data-testid="test-telegram-btn">
                  {testingTelegram ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Send Test
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ──── SMS Channel ──── */}
      <Card className={`border-2 transition-colors ${config.alert_via_sms ? 'border-emerald-300 bg-emerald-50/20' : 'border-gray-200'}`} data-testid="sms-channel-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.alert_via_sms ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                <Smartphone className={`h-5 w-5 ${config.alert_via_sms ? 'text-emerald-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">SMS Alerts</h3>
                <p className="text-xs text-gray-500">Text message notifications to your phone</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="sms-toggle">
              <input type="checkbox" className="sr-only peer" checked={config.alert_via_sms || false}
                onChange={e => setConfig({...config, alert_via_sms: e.target.checked})} />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          <div className="space-y-3">
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">Phone Number</p>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                <input type="tel" value={config.alert_sms_phone || ''} onChange={e => setConfig({...config, alert_sms_phone: e.target.value || null})}
                  placeholder="+1 (555) 123-4567"
                  className="flex-1 border rounded-md px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-emerald-200 outline-none transition" data-testid="sms-phone-input" />
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">Enter your mobile number with country code (e.g., +1 for US)</p>
            </div>

            {config.alert_sms_phone && (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-xs text-green-700 font-medium">Phone configured: {config.alert_sms_phone}</span>
                </div>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={testSms} disabled={testingSms} data-testid="test-sms-btn">
                  {testingSms ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Send Test
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ──── In-App Push Channel ──── */}
      <Card className={`border-2 transition-colors ${config.alert_via_push ? 'border-violet-300 bg-violet-50/20' : 'border-gray-200'}`} data-testid="push-channel-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.alert_via_push ? 'bg-violet-100' : 'bg-gray-100'}`}>
                <Bell className={`h-5 w-5 ${config.alert_via_push ? 'text-violet-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">In-App Notifications</h3>
                <p className="text-xs text-gray-500">Browser push notifications and in-app alerts</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="push-toggle">
              <input type="checkbox" className="sr-only peer" checked={config.alert_via_push || false}
                onChange={e => setConfig({...config, alert_via_push: e.target.checked})} />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
            </label>
          </div>
          {config.alert_via_push && (
            <p className="text-xs text-violet-600 mt-3 ml-[52px]">Push notifications active. Alerts appear in your browser when the app is open.</p>
          )}
        </CardContent>
      </Card>

      {/* ──── Email Channel ──── */}
      <Card className={`border-2 transition-colors ${config.alert_via_email ? 'border-orange-300 bg-orange-50/20' : 'border-gray-200'}`} data-testid="email-channel-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.alert_via_email ? 'bg-orange-100' : 'bg-gray-100'}`}>
                <Receipt className={`h-5 w-5 ${config.alert_via_email ? 'text-orange-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Email Alerts</h3>
                <p className="text-xs text-gray-500">Detailed email notifications with full context</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="email-toggle">
              <input type="checkbox" className="sr-only peer" checked={config.alert_via_email || false}
                onChange={e => setConfig({...config, alert_via_email: e.target.checked})} />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
            </label>
          </div>
          {config.alert_via_email && (
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">Email Address</p>
              <input type="email" value={config.alert_email || ''} onChange={e => setConfig({...config, alert_email: e.target.value || null})}
                placeholder="owner@restaurant.com"
                className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-200 outline-none transition" data-testid="email-input" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ──── Daily Digest ──── */}
      <Card className={`border-2 transition-colors ${config.digest_enabled ? 'border-amber-300 bg-amber-50/20' : 'border-gray-200'}`} data-testid="digest-channel-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.digest_enabled ? 'bg-amber-100' : 'bg-gray-100'}`}>
                <Clock className={`h-5 w-5 ${config.digest_enabled ? 'text-amber-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Daily Digest</h3>
                <p className="text-xs text-gray-500">Morning summary of overnight activity via enabled channels</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="digest-toggle">
              <input type="checkbox" className="sr-only peer" checked={config.digest_enabled || false}
                onChange={e => setConfig({...config, digest_enabled: e.target.checked})} />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
            </label>
          </div>

          {config.digest_enabled && (
            <div className="space-y-3">
              <div className="bg-white rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">Delivery Time (UTC)</p>
                    <select value={config.digest_hour ?? 6} onChange={e => setConfig({...config, digest_hour: parseInt(e.target.value)})}
                      className="border rounded-md px-3 py-1.5 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-amber-200 outline-none" data-testid="digest-hour-select">
                      {Array.from({length: 24}, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00 UTC</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-1">Digest is sent once daily at this hour via Telegram & SMS</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={sendDigestNow} disabled={sendingDigest} data-testid="send-digest-now-btn">
                    {sendingDigest ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                    Send Now
                  </Button>
                </div>
              </div>

              {/* Digest Preview */}
              {!digestPreview && (
                <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={loadDigestPreview} data-testid="preview-digest-btn">
                  Preview digest data
                </Button>
              )}
              {digestPreview && (
                <div className="bg-gray-50 rounded-lg border p-3 text-xs space-y-2" data-testid="digest-preview">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">Digest Preview (Last 24h)</span>
                    <button className="text-gray-400 hover:text-gray-600" onClick={() => setDigestPreview(null)}>
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-white rounded p-2 text-center border">
                      <p className="text-lg font-bold text-red-600">{digestPreview.alerts?.total || 0}</p>
                      <p className="text-[10px] text-gray-500">Alerts</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center border">
                      <p className="text-lg font-bold text-amber-600">{digestPreview.alerts?.unresolved || 0}</p>
                      <p className="text-[10px] text-gray-500">Unresolved</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center border">
                      <p className="text-lg font-bold text-gray-800">{digestPreview.removals?.total_count || 0}</p>
                      <p className="text-[10px] text-gray-500">Removals</p>
                    </div>
                    <div className="bg-white rounded p-2 text-center border">
                      <p className="text-lg font-bold text-gray-800">${digestPreview.removals?.total_value || 0}</p>
                      <p className="text-[10px] text-gray-500">Value Lost</p>
                    </div>
                  </div>
                  {digestPreview.top_employees?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-medium text-gray-600 mb-1">Top employees by removals:</p>
                      {digestPreview.top_employees.map((emp, i) => (
                        <p key={i} className="text-[10px] text-gray-500">{i + 1}. {emp.name}: {emp.count} items (${emp.value})</p>
                      ))}
                    </div>
                  )}
                  {digestPreview.revenue?.total > 0 && (
                    <p className="text-[10px] text-gray-500">Revenue: ${digestPreview.revenue.total} | Loss: {digestPreview.revenue.loss_percentage}%</p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-2">
        <Button onClick={saveConfig} disabled={saving} data-testid="save-notification-settings-btn" className="px-6">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Settings className="h-4 w-4 mr-2" />}
          Save All Settings
        </Button>
      </div>
    </div>
  );
}


/* ═══════════════════ Fraud Alerts Tab ═══════════════════ */
function FraudAlertsTab({ token, period }) {
  const [alerts, setAlerts] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  const loadAll = useLatestCallback(async () => {
    setLoading(true);
    try {
      const params = `period=${period}${filterStatus !== 'all' ? `&status=${filterStatus}` : ''}`;
      const [aRes, cRes] = await Promise.all([
        axios.get(`${API}/reports/fraud-alerts?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/fraud-alerts/config`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAlerts(aRes.data);
      setConfig(cRes.data);
    } catch { setAlerts(null); setConfig(null); }
    setLoading(false);
  });

  useEffect(() => { loadAll(); }, [period, filterStatus, loadAll]);

  const acknowledgeAlert = async (id) => {
    try {
      await axios.put(`${API}/reports/fraud-alerts/${id}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Alert acknowledged');
      loadAll();
    } catch { toast.error('Failed to acknowledge'); }
  };

  const dismissAlert = async (id) => {
    try {
      await axios.put(`${API}/reports/fraud-alerts/${id}/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Alert dismissed');
      loadAll();
    } catch { toast.error('Failed to dismiss'); }
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/reports/fraud-alerts/config`, config, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success('Thresholds updated');
      setShowConfig(false);
    } catch { toast.error('Failed to save config'); }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;
  if (!alerts) return <div className="py-12 text-center text-gray-400">No alert data</div>;

  const severityStyles = {
    critical: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };
  const severityDot = { critical: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-blue-500' };

  return (
    <div className="space-y-4" data-testid="fraud-alerts-tab">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={alerts.critical > 0 ? 'bg-red-50 border-red-200' : ''}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{alerts.critical}</p>
            <p className="text-xs text-red-500 font-medium">CRITICAL</p>
          </CardContent>
        </Card>
        <Card className={alerts.active > 0 ? 'bg-amber-50 border-amber-200' : ''}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{alerts.active}</p>
            <p className="text-xs text-amber-500 font-medium">ACTIVE ALERTS</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-600">{alerts.total}</p>
            <p className="text-xs text-gray-500 font-medium">TOTAL THIS PERIOD</p>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36"><Filter className="h-4 w-4 mr-2 text-gray-400" /><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)} data-testid="config-thresholds-btn">
          <ShieldAlert className="h-4 w-4 mr-2" /> Thresholds
        </Button>
      </div>

      {/* Config Panel (collapsible) - Thresholds only */}
      {showConfig && config && (
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Fraud Alert Thresholds</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Printed removals per shift</label>
                <input type="number" value={config.printed_removals_per_shift} onChange={e => setConfig({...config, printed_removals_per_shift: parseInt(e.target.value) || 0})}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="threshold-printed" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Voids per shift</label>
                <input type="number" value={config.voids_per_shift} onChange={e => setConfig({...config, voids_per_shift: parseInt(e.target.value) || 0})}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="threshold-voids" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Discount $ per shift</label>
                <input type="number" step="0.01" value={config.discount_amount_per_shift} onChange={e => setConfig({...config, discount_amount_per_shift: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="threshold-discount" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Daily total removed $</label>
                <input type="number" step="0.01" value={config.total_value_removed_per_day} onChange={e => setConfig({...config, total_value_removed_per_day: parseFloat(e.target.value) || 0})}
                  className="w-full border rounded px-2 py-1.5 text-sm" data-testid="threshold-daily" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Shift window (hours)</label>
                <input type="number" value={config.shift_window_hours} onChange={e => setConfig({...config, shift_window_hours: parseInt(e.target.value) || 8})}
                  className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={config.is_active} onChange={e => setConfig({...config, is_active: e.target.checked})} /> Active
                </label>
              </div>
            </div>
            <Button size="sm" onClick={saveConfig} data-testid="save-thresholds-btn">Save Thresholds</Button>
          </CardContent>
        </Card>
      )}

      {/* Alert List */}
      <div className="space-y-2">
        {alerts.alerts.map((alert) => (
          <Card key={alert.id} className={`border ${severityStyles[alert.severity] || 'bg-gray-50'} ${alert.status === 'dismissed' ? 'opacity-50' : ''}`}
            data-testid={`fraud-alert-${alert.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2.5 h-2.5 rounded-full ${severityDot[alert.severity] || 'bg-gray-400'} ${alert.status === 'active' ? 'animate-pulse' : ''}`} />
                    <span className="font-semibold text-sm">{alert.title}</span>
                    <Badge variant="outline" className="text-[10px] capitalize">{alert.status}</Badge>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{alert.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                    <span>{alert.created_at ? formatDateTime(alert.created_at, alerts?.timezone) : ''}</span>
                    {alert.trigger_value != null && <span>Value: {alert.trigger_value} / Threshold: {alert.threshold_value}</span>}
                    {alert.acknowledged_by && <span>Ack: {alert.acknowledged_by}</span>}
                  </div>
                </div>
                {alert.status === 'active' && (
                  <div className="flex gap-1 shrink-0 ml-3">
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => acknowledgeAlert(alert.id)}
                      data-testid={`ack-alert-${alert.id}`}>
                      <Eye className="h-3 w-3 mr-1" /> Acknowledge
                    </Button>
                    <Button variant="ghost" size="sm" className="text-xs h-7 text-gray-400" onClick={() => dismissAlert(alert.id)}
                      data-testid={`dismiss-alert-${alert.id}`}>
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {alerts.alerts.length === 0 && <div className="py-12 text-center text-gray-400">No fraud alerts in this period</div>}
      </div>
    </div>
  );
}


/* ═══════════════════ Requested Reports Tab ═══════════════════ */
function RequestedReportsTab({ token, period }) {
  const [busy, setBusy] = useState(false);
  const exportCsv = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/reports/export/sales.csv?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const name = (r.headers?.['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `sales_${period}.csv`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error('Could not export the sales report. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="requested-reports-tab">
      <Card className="bg-white">
        <CardContent className="p-8 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Requested Reports</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
            Sales by day for the selected period, on the restaurant's own clock: money taken, refunds &amp; voids,
            revenue and tips - the same figures as the dashboard and the sales overview.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={exportCsv} disabled={busy} data-testid="export-sales-csv">
              <Download className="h-4 w-4 mr-2" /> {busy ? 'Exporting...' : 'Export sales as CSV'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* ═══════════════════ MAIN REPORTS PAGE ═══════════════════ */
export default function ReportsPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token') } = context;

  const [period, setPeriod] = useState('today');
  const [activeTab, setActiveTab] = useState('sales-overview');
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs = [
    { id: 'sales-overview', label: 'Sales Overview', icon: BarChart3 },
    { id: 'end-of-day', label: 'End of Day', icon: Receipt },
    { id: 'sales-report', label: 'Sales Report', icon: FileText },
    { id: 'tender-card', label: 'Tender & Card Types', icon: CreditCard },
    { id: 'item-sales', label: 'Item Sales', icon: Package },
    { id: 'removed-items', label: 'Removed Items', icon: Trash2 },
    { id: 'fraud-alerts', label: 'Fraud Alerts', icon: ShieldAlert },
    { id: 'notification-setup', label: 'Notification Setup', icon: Bell },
    { id: 'requested', label: 'Requested Reports', icon: Download },
    { id: 'order-types', label: 'Order Types', icon: ShoppingBag },
    { id: 'employees', label: 'Employees', icon: Users },
    { id: 'discounts', label: 'Discounts', icon: Tag },
  ];

  return (
    <div className="space-y-4" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500 text-sm">Business performance insights</p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodSelect value={period} onChange={setPeriod} />
          <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)} data-testid="refresh-reports-btn" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tab Navigation — horizontal scrollable on mobile, matching Clover's menu */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-1 min-w-max bg-gray-100 p-1 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                data-testid={`report-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all whitespace-nowrap
                  ${isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-2" key={refreshKey}>
        {activeTab === 'sales-overview' && <SalesOverviewTab token={token} period={period} />}
        {activeTab === 'end-of-day' && <EndOfDayReport token={token} />}
        {activeTab === 'sales-report' && <SalesReportTab token={token} period={period} />}
        {activeTab === 'tender-card' && <TenderCardTypesTab token={token} period={period} />}
        {activeTab === 'item-sales' && <ItemSalesTab token={token} period={period} />}
        {activeTab === 'removed-items' && <RemovedItemsTab token={token} period={period} />}
        {activeTab === 'fraud-alerts' && <FraudAlertsTab token={token} period={period} />}
        {activeTab === 'notification-setup' && <NotificationSetupTab token={token} />}
        {activeTab === 'requested' && <RequestedReportsTab token={token} period={period} />}
        {activeTab === 'order-types' && <OrderTypesTab token={token} period={period} />}
        {activeTab === 'employees' && <EmployeesTab token={token} period={period} />}
        {activeTab === 'discounts' && <DiscountsTab token={token} period={period} />}
      </div>
    </div>
  );
}
